import { useFormContext } from 'react-hook-form';
import { PersonaFormBlock } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/PersonaFormBlock';
import { AiNameField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/AiNameField/AiNameField';
import { ProjectNameField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ProjectNameField/ProjectNameField';
import { clsx } from 'clsx';
import { ProjectDescriptionField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ProjectDescriptionField/ProjectDescriptionField';
import { ProjectLinksField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ProjectLinksField/ProjectLinksField';
import { RoleField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/RoleField/RoleField';
import { RoleDescriptionField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/RoleDescriptionField/RoleDescriptionField';
import { ToneOfVoiceChannelField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ToneOfVoiceFields/ToneOfVoiceChannelField/ToneOfVoiceChannelField';
import { ToneOfVoiceEmojiField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ToneOfVoiceFields/ToneOfVoiceEmojiField/ToneOfVoiceEmojiField';
import { ToneOfVoiceStyleDescriptionField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ToneOfVoiceFields/ToneOfVoiceStyleDescriptionField/ToneOfVoiceStyleDescriptionField';
import { ToneOfVoiceStyleField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ToneOfVoiceFields/ToneOfVoiceStyleField/ToneOfVoiceStyleField';
import { ToneOfVoiceResponseLengthField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ToneOfVoiceFields/ToneOfVoiceResponseLengthField/ToneOfVoiceResponseLengthField';
import { ToneOfVoiceResponseLengthDescriptionField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ToneOfVoiceFields/ToneOfVoiceResponseLengthDescriptionField/ToneOfVoiceResponseLengthDescriptionField';
import { DefaultLanguageField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/DefaultLanguageField/DefaultLanguageField';
import { ExcludedLanguagesField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/ExcludedLanguagesField/ExcludedLanguagesField';
import { WordsBlackListField } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/WordsBlackListField/WordsBlackListField';
import type { ConfigurationFormValues } from 'src/pages/ConfigurationPage/hooks/useConfigurationForm';
import type { PersonaInputData } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';
import type { ConfigurationFormFieldsObject } from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';

export const PersonaForm = ({
  formFieldsObject = {},
  inputData = {},
  readonly = true,
  compact = false,
}: {
  formFieldsObject?: ConfigurationFormFieldsObject;
  inputData?: PersonaInputData | null;
  readonly?: boolean;
  compact?: boolean;
}) => {
  const { control, setValue, watch } = useFormContext<ConfigurationFormValues>();

  const websiteLinks = watch('pf_pages');
  const role = watch('pf_role');
  const roleClickValue = watch('pf_role_click_value');
  const style = watch('pf_tone_of_voice_style');
  const responseLength = watch('pf_tone_of_voice_resp_len');

  return (
    <div className={'flex !flex-col gap-5'}>
      <PersonaFormBlock
        label={'AI Identity'}
        compact={compact}
        fields={[formFieldsObject.pf_tone_of_voice_channel]}
      >
        <AiNameField
          field={formFieldsObject.pf_ai_name}
          data={inputData?.pf_ai_name}
          setValue={setValue}
          readonly={readonly}
          control={control}
          compact={compact}
        />
      </PersonaFormBlock>
      <PersonaFormBlock
        label={'Company Info'}
        compact={compact}
        fields={[
          formFieldsObject.pf_company_name,
          formFieldsObject.pf_company_description,
          formFieldsObject.pf_pages,
        ]}
      >
        <div className={clsx('flex w-full gap-2.5 flex-col')}>
          <div className={clsx('flex w-full gap-2.5', compact ? 'flex-col' : 'flex-row')}>
            <ProjectNameField
              field={formFieldsObject.pf_company_name}
              data={inputData?.pf_company_name}
              setValue={setValue}
              readonly={readonly}
              control={control}
              compact={compact}
            />
            <ProjectDescriptionField
              field={formFieldsObject.pf_company_description}
              data={inputData?.pf_company_description}
              setValue={setValue}
              readonly={readonly}
              control={control}
              compact={compact}
            />
          </div>
          <ProjectLinksField
            field={formFieldsObject.pf_pages}
            data={inputData?.pf_pages}
            readonly={readonly}
            setValue={setValue}
            compact={compact}
            value={websiteLinks}
          />
        </div>
      </PersonaFormBlock>
      <PersonaFormBlock
        label={'Role'}
        compact={compact}
        fields={[formFieldsObject.pf_role, formFieldsObject.pf_role_custom]}
      >
        <div className={'flex flex-col w-full gap-3.5'}>
          <RoleField
            field={formFieldsObject.pf_role}
            data={inputData?.pf_role}
            readonly={readonly}
            control={control}
            compact={compact}
            watch={watch}
            setValue={setValue}
          />
          <RoleDescriptionField
            field={formFieldsObject.pf_role_custom}
            salesRoleFields={{
              pf_cta: formFieldsObject.pf_cta,
              pf_icp: formFieldsObject.pf_icp,
            }}
            data={inputData?.pf_role_custom}
            salesRoleData={{
              pf_cta: inputData?.pf_cta,
              pf_icp: inputData?.pf_icp,
            }}
            readonly={readonly}
            control={control}
            compact={compact}
            setValue={setValue}
            role={role}
            watch={watch}
            roleClickValue={roleClickValue}
          />
        </div>
      </PersonaFormBlock>
      <PersonaFormBlock
        label={'Tone of voice'}
        compact={compact}
        fields={[
          formFieldsObject.pf_tone_of_voice_channel,
          formFieldsObject.pf_tone_of_voice_style,
          formFieldsObject.pf_tone_of_voice_style_custom,
          formFieldsObject.pf_tone_of_voice_emoji,
          formFieldsObject.pf_tone_of_voice_resp_len,
        ]}
      >
        <div className={'flex flex-col w-full gap-3.5'}>
          <ToneOfVoiceChannelField
            field={formFieldsObject.pf_tone_of_voice_channel}
            data={inputData?.pf_tone_of_voice_channel}
            readonly={readonly}
            control={control}
            compact={compact}
            watch={watch}
            setValue={setValue}
          />
          <ToneOfVoiceStyleField
            field={formFieldsObject.pf_tone_of_voice_style}
            data={inputData?.pf_tone_of_voice_style}
            readonly={readonly}
            control={control}
            compact={compact}
            watch={watch}
            setValue={setValue}
          />
          <ToneOfVoiceStyleDescriptionField
            field={formFieldsObject.pf_tone_of_voice_style_custom}
            data={inputData?.pf_tone_of_voice_style_custom}
            readonly={readonly}
            control={control}
            compact={compact}
            setValue={setValue}
            watch={watch}
            style={style}
          />
          <ToneOfVoiceEmojiField
            field={formFieldsObject.pf_tone_of_voice_emoji}
            data={inputData?.pf_tone_of_voice_emoji}
            readonly={readonly}
            control={control}
            compact={compact}
            watch={watch}
            setValue={setValue}
          />
          <ToneOfVoiceResponseLengthField
            field={formFieldsObject.pf_tone_of_voice_resp_len}
            data={inputData?.pf_tone_of_voice_resp_len}
            readonly={readonly}
            control={control}
            compact={compact}
            watch={watch}
            setValue={setValue}
          />
          <ToneOfVoiceResponseLengthDescriptionField
            field={formFieldsObject.pf_tone_of_voice_resp_len}
            responseLength={responseLength}
            compact={compact}
          />
        </div>
      </PersonaFormBlock>
      <PersonaFormBlock
        label={'Language'}
        compact={compact}
        fields={[formFieldsObject.pf_default_language, formFieldsObject.pf_excluded_languages]}
      >
        <div className={clsx('flex w-full gap-3.5', compact ? 'flex-col' : 'flex-row')}>
          <DefaultLanguageField
            field={formFieldsObject.pf_default_language}
            data={inputData?.pf_default_language}
            readonly={readonly}
            control={control}
            compact={compact}
            watch={watch}
            setValue={setValue}
          />
          <ExcludedLanguagesField
            field={formFieldsObject.pf_excluded_languages}
            data={inputData?.pf_excluded_languages}
            readonly={readonly}
            control={control}
            compact={compact}
            setValue={setValue}
            watch={watch}
          />
        </div>
      </PersonaFormBlock>
      <PersonaFormBlock
        label={'Restrictions'}
        compact={compact}
        fields={[formFieldsObject.pf_words_blacklist]}
      >
        <WordsBlackListField
          field={formFieldsObject.pf_words_blacklist}
          data={inputData?.pf_words_blacklist}
          readonly={readonly}
          control={control}
          compact={compact}
          setValue={setValue}
          watch={watch}
        />
      </PersonaFormBlock>
    </div>
  );
};
